import { useState, type FormEvent } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input, Select, Textarea } from "./ui/input";
import { Button } from "./ui/button";
import type { Initiative, Priority, Product, Status, Team } from "@/lib/types";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initial?: Partial<Initiative>;
  teams: Team[];
  products: Product[];
  defaultQuarter: string;
  onSubmit: (data: Partial<Initiative>) => Promise<void>;
}

export function InitiativeForm({
  open,
  onOpenChange,
  initial,
  teams,
  products,
  defaultQuarter,
  onSubmit,
}: Props) {
  const editing = Boolean(initial?.id);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    const fd = new FormData(e.currentTarget);
    const data: Partial<Initiative> = {
      title: String(fd.get("title") ?? "").trim(),
      description: String(fd.get("description") ?? "").trim() || null,
      team_id: Number(fd.get("team_id")),
      product_id: Number(fd.get("product_id")),
      quarter: String(fd.get("quarter") ?? defaultQuarter),
      start_date: String(fd.get("start_date") ?? ""),
      end_date: String(fd.get("end_date") ?? ""),
      status: fd.get("status") as Status,
      priority: fd.get("priority") as Priority,
      owner: String(fd.get("owner") ?? "").trim() || null,
      jira_key: String(fd.get("jira_key") ?? "").trim() || null,
    };
    try {
      await onSubmit(data);
      onOpenChange(false);
    } catch (err: any) {
      setError(err?.message ?? "submit failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogTitle>{editing ? "Edit initiative" : "New initiative"}</DialogTitle>
        <DialogDescription>
          {editing ? "Update fields and save." : "Add a quarterly initiative. All visible to stakeholders."}
        </DialogDescription>

        <form onSubmit={handleSubmit} className="mt-4 grid gap-3">
          <Field label="Title">
            <Input name="title" defaultValue={initial?.title ?? ""} required maxLength={200} />
          </Field>

          <Field label="Description">
            <Textarea name="description" defaultValue={initial?.description ?? ""} rows={3} />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Team">
              <Select name="team_id" defaultValue={initial?.team_id ?? teams[0]?.id} required>
                {teams.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Product">
              <Select name="product_id" defaultValue={initial?.product_id ?? products[0]?.id} required>
                {products.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </Select>
            </Field>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <Field label="Quarter">
              <Input
                name="quarter"
                defaultValue={initial?.quarter ?? defaultQuarter}
                pattern="\d{4}-Q[1-4]"
                placeholder="2026-Q3"
                required
              />
            </Field>
            <Field label="Start">
              <Input
                type="date"
                name="start_date"
                defaultValue={initial?.start_date ?? ""}
                required
              />
            </Field>
            <Field label="End">
              <Input
                type="date"
                name="end_date"
                defaultValue={initial?.end_date ?? ""}
                required
              />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Status">
              <Select name="status" defaultValue={initial?.status ?? "planned"} required>
                <option value="planned">Planned</option>
                <option value="in_progress">In progress</option>
                <option value="done">Done</option>
                <option value="cancelled">Cancelled</option>
              </Select>
            </Field>
            <Field label="Priority">
              <Select name="priority" defaultValue={initial?.priority ?? "P1"} required>
                <option value="P0">P0</option>
                <option value="P1">P1</option>
                <option value="P2">P2</option>
              </Select>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Owner">
              <Input name="owner" defaultValue={initial?.owner ?? ""} placeholder="e.g. Yehor M." />
            </Field>
            <Field label="Jira key">
              <Input
                name="jira_key"
                defaultValue={initial?.jira_key ?? ""}
                placeholder="DEV-12345"
              />
            </Field>
          </div>

          {error && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 mt-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? "Saving…" : editing ? "Save" : "Create"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="grid gap-1.5">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
